import type { Editor, JSONContent } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import type {
  MarkdownTableCell,
  MarkdownTableData,
  MarkdownTableEditHandlers,
} from '@cotera/client/app/components/ui/data-grid/markdown-table';
import { InlineMarkdown } from './inline-markdown';

/** Absolute document positions bounding one table cell node. */
type CellBounds = { start: number; end: number };

type TableGeometry = {
  /** `rows[rowIndex][columnIndex]` — row 0 is the header row. */
  rows: CellBounds[][];
};

const forEachChild = (node: PMNode): PMNode[] => {
  const children: PMNode[] = [];
  node.forEach((child) => children.push(child));
  return children;
};

/**
 * Positions of every cell in the table. `getPos()` is the position before the
 * table node, so its content starts one further in; each row and cell is walked
 * by node size from there.
 */
export const computeTableGeometry = (
  tableNode: PMNode,
  tablePos: number
): TableGeometry => {
  const rows: CellBounds[][] = [];
  let rowOffset = tablePos + 1;
  tableNode.forEach((rowNode) => {
    const cells: CellBounds[] = [];
    let cellOffset = rowOffset + 1;
    rowNode.forEach((cellNode) => {
      cells.push({ start: cellOffset, end: cellOffset + cellNode.nodeSize });
      cellOffset += cellNode.nodeSize;
    });
    rows.push(cells);
    rowOffset += rowNode.nodeSize;
  });
  return { rows };
};

const serializeCellMarkdown = (editor: Editor, cellNode: PMNode): string => {
  const manager = editor.storage.markdown?.manager;
  const contentJson = cellNode.content.toJSON();
  const doc = {
    type: 'doc',
    content: Array.isArray(contentJson) ? contentJson : [],
  };
  const serialized = manager?.serialize(doc);
  return typeof serialized === 'string'
    ? serialized.trim()
    : cellNode.textContent.trim();
};

const parseMarkdownToBlocks = (
  editor: Editor,
  markdown: string
): JSONContent[] => {
  const manager = editor.storage.markdown?.manager;
  const parsed = manager?.parse(markdown);
  const content = parsed?.content;
  if (Array.isArray(content) && content.length > 0) {
    return content;
  }
  // A cell is `block+`, so it can never be truly empty.
  return [{ type: 'paragraph' }];
};

const cellFromNode = (editor: Editor, cellNode: PMNode): MarkdownTableCell => {
  const text = serializeCellMarkdown(editor, cellNode);
  return {
    text,
    content: text === '' ? null : <InlineMarkdown text={text} />,
  };
};

/** Turn the live table node into the grid's data model (row 0 = header). */
export const deriveTableData = (
  editor: Editor,
  tableNode: PMNode
): MarkdownTableData => {
  const rowNodes = forEachChild(tableNode);
  const [headerRow, ...bodyRows] = rowNodes;
  const headers =
    headerRow === undefined
      ? []
      : forEachChild(headerRow).map((cell) => cellFromNode(editor, cell));
  const rows = bodyRows.map((rowNode) =>
    forEachChild(rowNode).map((cell) => cellFromNode(editor, cell))
  );
  return { headers, rows };
};

const replaceCellContent = (
  editor: Editor,
  cell: CellBounds,
  markdown: string
): void => {
  const blocks = parseMarkdownToBlocks(editor, markdown);
  editor
    .chain()
    .insertContentAt({ from: cell.start + 1, to: cell.end - 1 }, blocks)
    .run();
};

/**
 * Run a table transform (`addRowAfter`, `deleteColumn`, …) against a specific
 * cell by first dropping the selection inside it. The stock table commands act
 * on the current selection, so this is how a node-view button targets a cell it
 * did not click into.
 */
const runInCell = (
  editor: Editor,
  cell: CellBounds,
  run: (chain: ReturnType<Editor['chain']>) => ReturnType<Editor['chain']>
): void => {
  // Land inside the cell's first paragraph's inline content (cell open + block
  // open = +2), clamped for an empty cell, so ProseMirror doesn't warn about a
  // selection endpoint that isn't in inline content.
  const inlinePos = Math.min(cell.start + 2, cell.end - 2);
  run(editor.chain().focus().setTextSelection(inlinePos)).run();
};

/**
 * The write side of the editable grid. Every handler recomputes geometry from
 * the latest node/position so it stays correct after earlier edits shifted the
 * document.
 */
export const createTableEditHandlers = (
  editor: Editor,
  getNode: () => PMNode,
  getTablePos: () => number | undefined
): MarkdownTableEditHandlers => {
  const geometry = (): TableGeometry | null => {
    const pos = getTablePos();
    if (pos === undefined) {
      return null;
    }
    return computeTableGeometry(getNode(), pos);
  };

  return {
    onCellChange: (rowIndex, columnIndex, value) => {
      // Body rows sit below the header row, hence the +1.
      const cell = geometry()?.rows[rowIndex + 1]?.[columnIndex];
      if (cell !== undefined) {
        replaceCellContent(editor, cell, value);
      }
    },
    onHeaderChange: (columnIndex, value) => {
      const cell = geometry()?.rows[0]?.[columnIndex];
      if (cell !== undefined) {
        replaceCellContent(editor, cell, value);
      }
    },
    onAddRow: () => {
      const rows = geometry()?.rows;
      const cell = rows?.[rows.length - 1]?.[0];
      if (cell !== undefined) {
        runInCell(editor, cell, (chain) => chain.addRowAfter());
      }
    },
    onRemoveRow: (rowIndex) => {
      const cell = geometry()?.rows[rowIndex + 1]?.[0];
      if (cell !== undefined) {
        runInCell(editor, cell, (chain) => chain.deleteRow());
      }
    },
    onAddColumn: () => {
      const headerRow = geometry()?.rows[0];
      const cell = headerRow?.[headerRow.length - 1];
      if (cell !== undefined) {
        runInCell(editor, cell, (chain) => chain.addColumnAfter());
      }
    },
    onRemoveColumn: (columnIndex) => {
      const headerRow = geometry()?.rows[0];
      // Deleting the last column would collapse the table to nothing.
      if (headerRow === undefined || headerRow.length <= 1) {
        return;
      }
      const cell = headerRow[columnIndex];
      if (cell !== undefined) {
        runInCell(editor, cell, (chain) => chain.deleteColumn());
      }
    },
  };
};
